import { z } from 'zod'

import { ActionType, AutomationActionType } from '../enums'

import { trashPageMetaSchema } from './trash'

// Backend sends 'actionId' instead of 'id'
export const automationActionSchema = z
  .object({
    actionId: z.string().uuid().nullable(),
    type: z.nativeEnum(AutomationActionType),
    metadata: z.record(z.unknown()),
    maxRetries: z.number().int().nonnegative(),
  })
  .transform((action) => ({
    id: action.actionId,
    type: action.type,
    metadata: action.metadata,
    maxRetries: action.maxRetries,
  }))

/** Narrows a trigger (calls, tags, changed fields); empty or missing lists match everything. */
export const automationTriggerFilterSchema = z.object({
  outcomes: z.array(z.string()).nullable().optional(),
  dispositionIds: z.array(z.string().uuid()).nullable().optional(),
  providerTypes: z.array(z.string()).nullable().optional(),
  tagIds: z.array(z.string().uuid()).nullable().optional(),
  fieldKeys: z.array(z.string()).nullable().optional(),
})
export type AutomationTriggerFilter = z.infer<typeof automationTriggerFilterSchema>

// Backend sends 'automationId' instead of 'id'.
// createdAt/updatedAt are not included in the response DTOs.
export const automationSchema = z
  .object({
    automationId: z.string().uuid(),
    recordGroupId: z.string().uuid(),
    workspaceId: z.string().uuid().optional(),
    triggerEvent: z.nativeEnum(ActionType),
    actions: z.array(automationActionSchema),
    enabled: z.boolean(),
    triggerFilter: automationTriggerFilterSchema.nullable().optional(),
  })
  .transform((automation) => ({
    id: automation.automationId,
    recordGroupId: automation.recordGroupId,
    workspaceId: automation.workspaceId,
    triggerEvent: automation.triggerEvent,
    actions: automation.actions,
    enabled: automation.enabled,
    triggerFilter: automation.triggerFilter ?? null,
  }))

// The POST /automations endpoint returns DTOAutomationCreationResponse which
// does NOT include actions, createdAt or updatedAt.
export const createAutomationResponseSchema = z
  .object({
    automationId: z.string().uuid(),
    recordGroupId: z.string().uuid(),
    workspaceId: z.string().uuid().optional(),
    triggerEvent: z.nativeEnum(ActionType),
    enabled: z.boolean(),
    triggerFilter: automationTriggerFilterSchema.nullable().optional(),
  })
  .transform((r) => ({
    id: r.automationId,
    recordGroupId: r.recordGroupId,
    workspaceId: r.workspaceId,
    triggerEvent: r.triggerEvent,
    enabled: r.enabled,
    triggerFilter: r.triggerFilter ?? null,
    actions: [] as { id: string | null; type: AutomationActionType; metadata: Record<string, unknown> }[],
  }))

export const createAutomationRequestSchema = z.object({
  recordGroupId: z.string().uuid(),
  triggerEvent: z.nativeEnum(ActionType),
  actions: z.array(
    z.object({
      type: z.nativeEnum(AutomationActionType),
      metadata: z.record(z.unknown()),
    })
  ),
  // Parts the trigger does not accept are dropped by the server
  triggerFilter: automationTriggerFilterSchema.nullable().optional(),
})

export const updateAutomationRequestSchema = createAutomationRequestSchema

// Trash-specific schema matching DTOTrashAutomationResponse from the backend.
// Omits 'actions' which is not included in the trash DTO.
export const trashedAutomationItemSchema = z.object({
  id: z.string().uuid(),
  triggerEvent: z.nativeEnum(ActionType),
  enabled: z.boolean(),
  recordGroupId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  deletedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const trashedAutomationPageSchema = trashPageMetaSchema.extend({
  content: z.array(trashedAutomationItemSchema),
})

export type Automation = z.infer<typeof automationSchema>
export type AutomationAction = z.infer<typeof automationActionSchema>
export type CreateAutomationRequest = z.infer<typeof createAutomationRequestSchema>
export type UpdateAutomationRequest = z.infer<typeof updateAutomationRequestSchema>
export type TrashedAutomationItem = z.infer<typeof trashedAutomationItemSchema>
export type TrashedAutomationPage = z.infer<typeof trashedAutomationPageSchema>